import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageContainer, Button } from "../components/kit";
import VerificationBanner from "../components/VerificationBanner";
import ApplyModal from "../components/ApplyModal";
import InboxPeek from "../components/InboxPeek";
import { Zap, Sparkles, Rocket, Settings, ArrowRight } from "lucide-react";

const SETUP_SEEN = "vh_autopilot_setup_seen";   // matches AutopilotSetup — avoids re-nagging in a session

// Candidate "AI Apply" hub — a single workspace that unifies the three apply
// surfaces (Matches, Applications, Settings) under one nav item. Each tab is a
// nested route rendered through <Outlet>, so tabs are deep-linkable and the
// browser back button works. Tabs render in "embedded" mode (they skip their own
// page shell because the hub provides the header + tab bar).

// Order as shown in the row; `end` marks the index route (AutoPilot Jobs = default at /apply).
// An "Alert Match" tab sat first here until 2026-10-03, when job alerts were removed.
// `count` names a stats key shown as a badge only when > 0. Each tab carries its own colour identity —
// `grad` (active gradient, literal so Tailwind JIT keeps it) + `accent` (inactive tint + icon + glow).
const TABS = [
  { to: "/apply", end: true, label: "AutoPilot Jobs", icon: Sparkles,
    grad: "from-[#4F46E5] to-[#7C3AED]", accent: "#6D28D9" },
  { to: "/apply/tracker", label: "Application Tracker", icon: Rocket, count: "needsReview",
    grad: "from-[#10B981] to-[#059669]", accent: "#059669" },
  { to: "/apply/settings", label: "Settings", icon: Settings,
    grad: "from-[#64748B] to-[#475569]", accent: "#475569" },
];

function Stat({ value, label }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="font-bold text-[#0F172A]">{value == null ? "—" : value}</span>
      <span>{label}</span>
    </span>
  );
}

export default function AIApplyHub() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ applied: null, needsReview: 0, left: null, applyWindow: "month" });
  const [applyUrl, setApplyUrl] = useState(null);   // a Ready-to-apply card's job URL → tailoring modal
  // "Apply to a job link" — no URL yet, so this opens the modal on its paste step. Kept separate
  // from applyUrl, which auto-starts tailoring for a URL we already have.
  const [linkOpen, setLinkOpen] = useState(false);
  const [appliedOverrides, setAppliedOverrides] = useState({});   // apply_url → {draft_id,status} so a matches card flips to its status pill in real time (no refresh)
  const [needsSetup, setNeedsSetup] = useState(false);   // first-run Autopilot setup not finished

  // First-run: auto-launch the setup wizard once per session until it's completed; after that
  // (or if they skip it) show a "Finish setup" banner so they can return to it.
  useEffect(() => {
    api.get("/apply/profile").then(({ data }) => {
      const onboarded = !!data.profile?.onboarded;
      setNeedsSetup(!onboarded);
      if (!onboarded && !sessionStorage.getItem(SETUP_SEEN)) {
        sessionStorage.setItem(SETUP_SEEN, "1");
        navigate("/apply/setup");
      }
    }).catch(() => {});
  }, [navigate]);

  const refreshDraftStats = () => {
    api.get("/apply/drafts").then(({ data }) => {
      const ds = data.drafts || [];
      setStats((s) => ({ ...s,
        applied: ds.filter((d) => ["submitted", "queued", "submitting"].includes(d.status)).length,
        needsReview: ds.filter((d) => d.status === "draft").length }));
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/apply/drafts")
      .then(({ data }) => {
        const ds = data.drafts || [];
        setStats((s) => ({
          ...s,
          applied: ds.filter((d) => ["submitted", "queued", "submitting"].includes(d.status)).length,
          needsReview: ds.filter((d) => d.status === "draft").length,
        }));
      }).catch(() => {});
    // Both plans have an allowance now, so this is no longer Premium-only: Basic gets a one-time
    // fifteen. `ai_applies_window` says which it is, and the label changes accordingly — a free
    // trial must not be described as something that refills.
    api.get("/candidate/plan-usage")
      .then(({ data }) => setStats((s) => ({
        ...s,
        left: Math.max(0, (data.ai_applies_limit || 0) - (data.ai_applies_used ?? data.ai_applies_this_month ?? 0)),
        applyWindow: data.ai_applies_window || "month",
      })))
      .catch(() => {});
  }, []);

  return (
    <PageContainer width="full">
      {/* Header */}
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0025D8] mb-1.5">Candidate</div>
        <h1 className="font-display font-black text-3xl text-[#0F172A] flex items-center gap-2.5">
          <Zap className="w-7 h-7 text-[#0025D8]" /> Autopilot
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">Apply on autopilot — the AI fills every application, you approve before anything is submitted.</p>
        <div className="flex items-center gap-3 mt-3 text-sm text-muted-foreground flex-wrap">
          <Stat value={stats.applied} label="applied on Autopilot" />
          {stats.needsReview > 0 && (
            <>
              <span className="text-border">·</span>
              <Stat value={stats.needsReview} label="need review" />
            </>
          )}
          {stats.left != null && (
            <>
              <span className="text-border">·</span>
              <Stat value={stats.left}
                label={stats.applyWindow === "lifetime" ? "free applies left" : "AI applies left"} />
            </>
          )}
        </div>
      </div>

      {/* First-run: finish Autopilot setup */}
      {needsSetup && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-[#0025D8]/25 bg-gradient-to-br from-[#0025D8]/[0.07] to-[#7C3AED]/[0.05] p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0025D8] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold text-base text-[#0F172A]">Finish setting up Autopilot</div>
              <p className="text-sm text-muted-foreground mt-0.5">Takes about a minute — we fill it from your résumé and prepare your first applications.</p>
            </div>
          </div>
          <Button size="sm" onClick={() => navigate("/apply/setup")} className="flex-shrink-0">
            Finish setup <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Tab bar — colour-coded pills; active = gradient fill + glow + lift, inactive = soft tint.
          The inbox sits at the far right: employer mail is a different kind of thing from a tab
          (it arrives on its own schedule), so it gets its own control rather than a fifth pill. */}
      <div className="flex items-center gap-2.5 mb-8 pb-1 -mx-1 px-1">
        <div className="flex items-center gap-2.5 overflow-x-auto min-w-0 flex-1 pb-1 -mb-1">
        {TABS.map((t) => {
          const n = t.count ? stats[t.count] : 0;
          return (
            <NavLink key={t.to} to={t.to} end={t.end}
              className={({ isActive }) =>
                `group inline-flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-2xl text-[13.5px] font-semibold whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? `text-white shadow-lg -translate-y-0.5 bg-gradient-to-br ${t.grad}`
                    : "text-[#0F172A] hover:-translate-y-0.5 hover:shadow-md"}`}
              style={({ isActive }) =>
                isActive
                  ? { boxShadow: `0 12px 26px -8px ${t.accent}A6` }
                  : { background: `${t.accent}12`, border: `1px solid ${t.accent}24` }}>
              {({ isActive }) => (
                <>
                  <span className="grid place-items-center w-7 h-7 rounded-xl transition-colors flex-shrink-0"
                    style={isActive ? { background: "rgba(255,255,255,0.22)" } : { background: `${t.accent}22` }}>
                    <t.icon className="w-4 h-4" style={{ color: isActive ? "#fff" : t.accent }} />
                  </span>
                  {t.label}
                  {n > 0 && (
                    <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full leading-none tabular-nums"
                      style={isActive ? { background: "rgba(255,255,255,0.25)", color: "#fff" } : { background: t.accent, color: "#fff" }}>
                      {n}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
        </div>
        <InboxPeek />
      </div>

      <VerificationBanner />

      {/* Tabs render here; the AutoPilot Jobs tab reads onApply/appliedOverrides from this context so the
          tailoring modal + live status pills keep working across tab switches. */}
      <Outlet context={{ embedded: true, onApply: setApplyUrl, onNewLink: () => setLinkOpen(true), appliedOverrides }} />

      {/* Tailoring modal opened by a Ready-to-apply card (works from any tab) */}
      <ApplyModal open={linkOpen} onClose={() => setLinkOpen(false)} />

      <ApplyModal open={!!applyUrl} initialUrl={applyUrl || ""} autoStart
        onClose={() => setApplyUrl(null)}
        onChanged={(draft) => {
          refreshDraftStats();
          // Flip the matches card for this job to its live status pill immediately (no refresh needed).
          if (draft?.draft_id && applyUrl) {
            const key = applyUrl.replace(/\/+$/, "");
            setAppliedOverrides((m) => ({ ...m, [key]: { draft_id: draft.draft_id, status: draft.status || "queued" } }));
          }
        }} />
    </PageContainer>
  );
}
